import axiosInstance from "../../services/axiosConfig";

/**
 * companyAutomationService
 * ------------------------------------------------------------------
 * Thin client for the brand-side DM Automation REST API. All endpoints
 * are JWT-authenticated (cookie + Authorization header are attached
 * automatically by `axiosConfig`) and are guarded server-side to
 * brand-type companies only.
 *
 * Error handling philosophy:
 *  - Each method `throws` an error whose `message` is suitable for
 *    surfacing in a toast. The original `response` is attached so
 *    callers that need richer error UX (e.g. mapping a `code` field
 *    to a CTA) can inspect it.
 */

const BASE = "/company/dm-automation";

function unwrap(response) {
  return response?.data ?? null;
}

function explain(error, fallback) {
  const data = error?.response?.data;
  const message =
    data?.message ||
    data?.error?.message ||
    data?.error ||
    error?.message ||
    fallback;
  const wrapped = new Error(message);
  wrapped.response = error?.response;
  wrapped.code = data?.code;
  return wrapped;
}

export const companyAutomationService = {
  /**
   * Quick readiness probe used by the dashboard. Returns:
   *   { instagram: { connected, username, businessAccountId },
   *     automationCount, activeCount }
   */
  async getStatus() {
    try {
      const res = await axiosInstance.get(`${BASE}/status`);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to load automation status");
    }
  },

  async listAutomations() {
    try {
      const res = await axiosInstance.get(BASE);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to fetch automations");
    }
  },

  async getAutomationById(id) {
    try {
      const res = await axiosInstance.get(`${BASE}/${id}`);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to fetch automation");
    }
  },

  async createAutomation(payload) {
    try {
      const res = await axiosInstance.post(BASE, payload);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to create automation");
    }
  },

  async updateAutomation(id, payload) {
    try {
      const res = await axiosInstance.put(`${BASE}/${id}`, payload);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to update automation");
    }
  },

  async toggleAutomation(id) {
    try {
      const res = await axiosInstance.patch(`${BASE}/${id}/toggle`);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to toggle automation");
    }
  },

  async deleteAutomation(id) {
    try {
      const res = await axiosInstance.delete(`${BASE}/${id}`);
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to delete automation");
    }
  },

  /**
   * On-demand AI analysis of an automation post's comments.
   *
   * Cost note: the server only calls Gemini on a cache miss (or when
   * comments changed). This method is therefore safe to call on an
   * explicit user click, and must NEVER be wired to auto-run on mount.
   *
   * Returns: { success, cached, analysis, meta }
   */
  async analyzeComments(automationId) {
    try {
      const res = await axiosInstance.post(
        `${BASE}/${automationId}/comments/analyze`
      );
      return unwrap(res);
    } catch (err) {
      throw explain(err, "Failed to analyze comments");
    }
  },
};

export default companyAutomationService;
